import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ConfigEvent, Events } from "@app/core/models/event-map";
import { PublishSubscribeService } from "@app/core/services/publish-subscribe.service";
import { ControlMeasureHeaderApiService } from "@app/mf/services/control-measure-header-api.service";
import { YesNoComponent } from "@app/shared/components/modals/yes-no/yes-no.component";
import { ConfigData } from "@app/shared/models/config-data.model";
import { Property } from "@app/shared/models/property.model";
import { UserContextSelectors } from "@app/shared/state/user-context/user-context.selectors";
import { Store } from "@ngxs/store";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { catchError, concatMap, map, mergeMap, Observable, of, take, tap } from "rxjs";
import { FigureConfigApiService } from "../figureconfig-api.service";
import { SalesBasketApiService } from "../salesbasket-api.service";
import { SalestableApiService } from "../salestable-api.service";
import { UserService } from "../User.Service";
import { AppService } from "./app-internal.service";
import { ConstantsService } from "./constants-internal.service";


@Injectable({
    providedIn: 'root'
})
export class FigureConfigService {

    private readonly userInternalService = inject(UserService)
    private readonly appService = inject(AppService)
    private readonly figureconfigApiService = inject(FigureConfigApiService)
    private readonly salesTableApiService = inject(SalestableApiService)
    private readonly salesBasketApiService = inject(SalesBasketApiService)
    private readonly controlMeasureApiService = inject(ControlMeasureHeaderApiService)
    private readonly publishSubscribeService = inject(PublishSubscribeService)
    private readonly modalService = inject(BsModalService)
    private readonly router = inject(Router)
    private readonly store = inject(Store)

    configVariables = {
        ConfigData: undefined,
        ConfigImagesMap: Object.create(null),
        Line: undefined,
        IsLoading_Line: false,
        HasChanged: false,
        IsSubConfig: false,
        DisableInput_Parent: false,
        SalesBasket: undefined,
        SalesTable: undefined
    }

    messageType = {
        ERROR: 0,
        WARNING: 1,
        INFO: 3
    }

    configData: ConfigData = {
        ConfigId: '',
        ConfigType: '',
        ConfigTransId: '',
        IsSubConfig: false,
        DisableInput: false,
        HasSetConfig: false,
        VisualGroupId: 0,
        IsDuplicated_FirstConfig: false,
        CalledFromPbaVarNoRel: "",
        LineItemId: '',
        LineLetter: '',
        SetName: '',
        LineUnitPrice: 0,
        LineTotalPrice: 0,
        LinePriceIsPreliminary: false,
        HasAccessToLine: false,
        LineIsLoaded: false,
        State_Config: false,
        State_ControlMeasure: false,
        State_ControlMeasureOrLater: false,
        State_ControlMeasure_Ready: false,
        State_ControlMeasure_Started: false,
        State_ControlMeasure_Saved: false,
        State_ControlMeasure_Finished: false,
        State_ControlMeasure_Done: false,
        State_ControlMeasure_AtLeastDone: false,
        State_ControlMeasure_ConfirmedBySeller: false,
        State_ControlMeasure_DisableInput: false,
        State_ControlMeasure_Head_Finished: false,
        State_ControlMeasure_Head_AtLeastFinished: false,
        State_ControlMeasure_AllValueStatesChanged: false,
        SelectedField: '',
        SelectedAreaRecId: 0,
        SelectedDisplacementVarRecId: 0,
        SelectedProperty: {},
        SelectedProperty_ControlMeasure: {},
        SelectedProperty_ControlMeasure_HasChangedValueState: false,
        IsValidatingValue: false,
        ShowMeasurements: false,
        ErrorMessage: "",
        HasDisplacementWarnings: false,
        MouseEvent: undefined,
        IsChangeableOrder: false,

    }

    //Properties that should not trigger reload of any data
    readonly PBAVARNOREL_NO_RELOAD = ["littera", "webDocuRef_EndCust_MF", "webDocuRef_Assembler_MF", "webDocuRef_Sales_MF", "lineQty", "webDocuRef_SVF", "webDocuRef_EndCust"]
    private configVariablesStack = []
    private _config = this.configVariables
    private messageTypes = this.messageType
    private updatingLineDescriptions = false

    private isAssembler = this.store.selectSignal(UserContextSelectors.isAssembler)

    pushConfigToStack() {
        const salesBasketTmp = this._config.SalesBasket
        const salesTableTmp = this._config.SalesTable

        this.configVariablesStack.push(this._config)
        const disableInput_Parent = this._config.ConfigData.DisableInput

        this._config = this.configVariables
        this._config.IsSubConfig = true
        this._config.DisableInput_Parent = disableInput_Parent
        this._config.SalesBasket = salesBasketTmp
        this._config.SalesTable = salesTableTmp
    }

    popConfigFromStack() {
      this._config = this.configVariablesStack.pop();

        //191015PO --> Get price when we return from subconfig
        if (!this._config.IsSubConfig) {
            this.getLinePrice()
        }
        this.clearIsDoingMessage()

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-TRANS-ID-CHANGED' })
    }

    showIsDoingMessage(message) {
        this.publishSubscribeService.trigger<string>(Events.FigureConfigService.IS_DOING_MESSAGE, message)
    }

    clearIsDoingMessage() {
        this.publishSubscribeService.trigger<string>(Events.FigureConfigService.IS_DOING_MESSAGE, '')
    }

    // Called from figure-config.js when loading
    doOnConfigStart(configId: string, configTransId: string, configType: string, calledFromPbaVarNoRel: string, isDuplicated_FirstConfig: boolean): void {

        // If new config is sub-config
        if (this._config && this._config.ConfigData && this._config.ConfigData.ConfigId === configId && this._config.ConfigData.ConfigTransId !== configTransId) {

            // Push current config onto stack
            this.pushConfigToStack();
        }
        else {
            // Clear this flag (WebQuotationController.js uses this to reload operations)
            this.appService.figureConfigClosed_HasConfigChanged = false;
        }

        this.showIsDoingMessage('LÄSER IN KONFIGURATIONEN');

        switch (configType) {
            case 'sales':
                this.salesTableApiService.onConfigStartSales(configId, configTransId, calledFromPbaVarNoRel)
                    .pipe(
                        mergeMap((response) => {
                            if (response?.SalesId) {
                                return this.salesTableApiService.getSalesTable(response.SalesId)
                            }

                            return of(null)
                        })
                    ).subscribe({
                        next: (response) => {

                            if (!response) { this.showError("Ett fel inträffade. Försök igen senare eller kontakta supporten."); return }

                            const salesTable = response

                            this._config.SalesTable = salesTable
                            this.userInternalService.accessLevel(undefined, true, 0, this._config.SalesTable);

                            this.clearIsDoingMessage()

                            // Creates new ConfigData
                            this.setConfigData(configId, configTransId, configType, calledFromPbaVarNoRel, isDuplicated_FirstConfig, this._config.SalesTable.IS_CHANGABLE_ORDER > 0)

                            // Broadcast event that anyone interested of can pickup
                            const event = "CONFIG-START-EXECUTED"
                            this.publishSubscribeService.trigger<{ configData: any, event: string }>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: event })
                        },
                        error: (err) => {
                            this.showError("Kunde inte kontakta servern. Försök igen senare eller kontakta supporten.")
                        }
                    })

                break;

            case 'quotation':
                return

            case 'webquotation':
                this.salesBasketApiService.onConfigStartBasket(configId, configTransId, calledFromPbaVarNoRel)
                    .pipe(
                        concatMap((response) => {
                            if (response?.SalesBasketId) {
                                return this.salesBasketApiService.getSalesBasket(response.SalesBasketId)
                            }

                            return of(null)
                        })
                    ).subscribe({
                        next: (response) => {
                            if (!response) { this.showError("Ett fel inträffade. Försök igen senare eller kontakta supporten."); return }

                            const salesBasket = response

                            this._config.SalesBasket = salesBasket

                            this.userInternalService.accessLevel(this._config.SalesBasket, false, ConstantsService.Basket.Status.ENUM_CONFIRMEDWITHSALESID)

                            this.clearIsDoingMessage()

                            // Creates new ConfigData
                            this.setConfigData(configId, configTransId, configType, calledFromPbaVarNoRel, isDuplicated_FirstConfig, false)

                            // Broadcast event that anyone interested of can pickup
                            const event = "CONFIG-START-EXECUTED"
                            this.publishSubscribeService.trigger<{ configData: any, event: string }>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: event })
                        },
                        error: (err) => {
                            this.showError("Kunde inte kontakta servern. Försök igen senare eller kontakta supporten.")
                        }
                    })

                break;
        }
    }

    private executeConfigClose(isSubConfig, configId, configTransId, changeToLocation?): void {
        let accessLevel = this.userInternalService.getAccessLevel()
        const isState_ControlMeasure = this._config.ConfigData.State_ControlMeasure
        const allControlMeasuredValues_HasChangedState = this._config.ConfigData.State_ControlMeasure_AllValueStatesChanged

        //200903DL --> M1835_LåsaUppOffert
        accessLevel = this._config.ConfigData.DisableInput ? 0 : 1;

        if (isState_ControlMeasure && allControlMeasuredValues_HasChangedState) {
            accessLevel = 1;
        }

        if (accessLevel < 1) {
            this.afterUpdateLineDescriptions(isSubConfig, changeToLocation)
            return
        }

        if (this.userInternalService.getAccessLevel() > 0) {

            switch (this._config.ConfigData.ConfigType) {
                case 'sales':
                    this.isUpdatingLineDescriptions(true)

                    this.salesTableApiService.onConfigCloseSales(configId, configTransId, this._config.ConfigData.CalledFromPbaVarNoRel)
                        .pipe(
                            concatMap((response) => {
                                return this.salesTableApiService.updateLineDescriptions_Sales(this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId)
                            }),
                            tap(() => {
                                this.broadcastEventLineDescriptionsUpdated()
                            })
                        ).subscribe({
                            next: (response) => {
                                this.afterUpdateLineDescriptions(isSubConfig, changeToLocation)
                            },
                            error: (err) => {
                                this.showError("Kunde inte kontakta servern. Försök igen senare eller kontakta supporten.")
                                this.isUpdatingLineDescriptions(false)
                            }
                        })
                    break;

                case 'quotation':
                    return   // Not implemented yet

                case 'webquotation':
                    this.isUpdatingLineDescriptions(true)

                    this.salesBasketApiService.onConfigCloseBasket(configId, configTransId, this._config.ConfigData.CalledFromPbaVarNoRel)
                        .pipe(
                            concatMap((response) => {
                                return this.salesBasketApiService.updateLineDescriptionsBasket(this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId)
                            }),
                            tap(() => {
                                this.broadcastEventLineDescriptionsUpdated()
                            })
                        ).subscribe({
                            next: (response) => {
                                this.afterUpdateLineDescriptions(isSubConfig, changeToLocation)
                            },
                            error: (err) => {
                                this.showError("Kunde inte kontakta servern. Försök igen senare eller kontakta supporten.")
                                this.isUpdatingLineDescriptions(false)
                            }
                        })
                    break;
            }
        }

        return
    }

    // Called from figure-config.component.ts when closing
    doOnConfigClose() {

        let isSubConfig = false

        // If this is a subConfig we're closing
        if (this.configVariablesStack.length > 0) {

            this.showIsDoingMessage('STÄNGER SUB-KONFIGURATIONEN')

            isSubConfig = true

            // Execute things on close
            this.executeConfigClose(isSubConfig, this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId)

            return;
        }

        let changeToLocationWhenClosing: string = ''

        switch (this._config.ConfigData.ConfigType) {
            case 'sales':

                this.showIsDoingMessage('STÄNGER KONFIGURATORN')

                // Broadcast event that anyone interested of can pickup
                this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-IS-CLOSING' })

                changeToLocationWhenClosing = `/salesOrder/${this._config.ConfigData.ConfigId}/`
                this.executeConfigClose(isSubConfig, this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId, changeToLocationWhenClosing);

                break;

            case 'quotation':

                this.showIsDoingMessage('STÄNGER KONFIGURATORN')

                // Broadcast event that anyone interested of can pickup
                this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-IS-CLOSING' })

                changeToLocationWhenClosing = `/quotation/${this._config.ConfigData.ConfigId}/`
                this.executeConfigClose(isSubConfig, this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId, changeToLocationWhenClosing)
                break;

            case 'webquotation':

                this.showIsDoingMessage('STÄNGER KONFIGURATORN');

                // Broadcast event that anyone interested of can pickup
                this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-IS-CLOSING' })

                changeToLocationWhenClosing = `/webquotation/${this._config.ConfigData.ConfigId}/${this._config.ConfigData.ConfigTransId}`

                // Set Flag for outsiders (WebQuotationController.js) that the config has changed or not
                this.appService.figureConfigClosed_HasConfigChanged = this._config.HasChanged || this._config.ConfigData.IsDuplicated_FirstConfig;
                this.appService.figureConfigClosed_HasConfigNotChanged = !this.appService.figureConfigClosed_HasConfigChanged;
                this.executeConfigClose(isSubConfig, this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId, changeToLocationWhenClosing);
                break;
        }
    }

    private broadcastEventLineDescriptionsUpdated() {
        this.publishSubscribeService.trigger<string>(Events.FigureConfigService.UPDATED_LINE_DESCRIPTION, this._config.ConfigData.ConfigTransId)
        this.isUpdatingLineDescriptions(false)
    }

    private afterUpdateLineDescriptions(isSubConfig, changeToLocation) {

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<{ configData: any, event: string }>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-CLOSE-EXECUTED' });

        // If subConfig
        if (isSubConfig != undefined && isSubConfig === true) {

            // Pop parent config from Stack to continue from there
            this.popConfigFromStack();
        }

        // Change location
        if (changeToLocation != undefined) {
            this.router.navigateByUrl(changeToLocation);

            // Clear
            this._config.ConfigData.ConfigTransId = '';

            // Clear configData
            this._config.ConfigData = undefined;

            // Clear
            this._config = this.configVariables;
            this.configVariablesStack = [];
        }
    }

    setSelectedField(field: string, areaRecId: number) {
        this._config.ConfigData.SelectedField = field
        this._config.ConfigData.SelectedAreaRecId = areaRecId
        this._config.ConfigData.SelectedDisplacementVarRecId = 0

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'IMAGE-FIELD-SELECTED' });
    }

    setSelectedDisplacementVar(displacementVarRecId) {
        this._config.ConfigData.SelectedField = ''
        this._config.ConfigData.SelectedAreaRecId = displacementVarRecId
        this._config.ConfigData.SelectedDisplacementVarRecId = displacementVarRecId

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'IMAGE-DISPLACEMENT-VAR-SELECTED' });
    }

    setSelectedProperty(property: Property | {}) {
        this._config.ConfigData.SelectedProperty = property
        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'PROPERTY-SELECTED' });
    }

    setSelectedProperty_ControlMeasure(property?: Property) {
        // Set as selected ControlMeasure property
        this._config.ConfigData.SelectedProperty_ControlMeasure = property
        this._config.ConfigData.SelectedProperty_ControlMeasure_HasChangedValueState = this.hasControlMeasuredValue_ChangedState(this._config.ConfigData.SelectedProperty_ControlMeasure)

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONTROL-MEASURE-PROPERTY-SELECTED' });
    }

    deletedProperty_ControlMeasure() {
      // Set as selected ControlMeasure property
      this._config.ConfigData.SelectedProperty_ControlMeasure = undefined;

      // Broadcast event that anyone interested of can pickup
      this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONTROL-MEASURE-PROPERTY-DELETED' });
    }

    // SAVE CONTROL MEASURE
    saveSelectedProperty_ControlMeasure(controlMeasureProperty: Property) {

        let useValue = controlMeasureProperty.CONTROLMEASURE_VALUE;

        if (controlMeasureProperty.VALUE && (!useValue || useValue === '')) {
            useValue = controlMeasureProperty.VALUE;
        }

        let useDisplayValue = controlMeasureProperty.CONTROLMEASURE_DISPLAYVALUE;

        if (controlMeasureProperty.DISPLAYVALUE && (!useDisplayValue || useDisplayValue === '')) {
            useDisplayValue = controlMeasureProperty.DISPLAYVALUE;
        }

        this.controlMeasureApiService.saveControlMeasureValue(
            this._config.ConfigData.ConfigId,
            this._config.ConfigData.ConfigTransId,
            controlMeasureProperty.PBAVARNOREL,
            controlMeasureProperty.DATAAREAID,
            useValue,
            useDisplayValue,
            controlMeasureProperty.VALUE,
            controlMeasureProperty.DISPLAYVALUE,
            controlMeasureProperty.LABEL,
            controlMeasureProperty.CONTROLMEASURE_INFOTEXT)
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.onValueChanged()
                    } else {
                        this.showError(response.Message)
                    }
                }
            })
    }

    // SAVE CONTROL MEASURE - VALUE STATE
    saveSelectedProperty_ControlMeasureValueState(controlMeasureProperty: Property, valueState: number) {

        const executedFromControlMeasureProperty: boolean = true

        switch (valueState) {
            case ConstantsService.ControlMeasureValueState.NOT_SET:
            case ConstantsService.ControlMeasureValueState.NEW_VALUE:
                break;

            case ConstantsService.ControlMeasureValueState.ACCEPTED_BY_SELLER:
                this.validateNewValue(controlMeasureProperty.CONTROLMEASURE_VALUE, controlMeasureProperty, controlMeasureProperty.CONTROLMEASURE_DISPLAYVALUE, executedFromControlMeasureProperty);
                break;

            case ConstantsService.ControlMeasureValueState.REJECTED_BY_SELLER:
                this.validateNewValue(controlMeasureProperty.CONTROLMEASURE_VALUEORIG, controlMeasureProperty, controlMeasureProperty.CONTROLMEASURE_DISPLAYVALUEORIG, executedFromControlMeasureProperty);
                break;
        }

        this.controlMeasureApiService.saveControlMeasureValueState(
            this._config.ConfigData.ConfigId,
            this._config.ConfigData.ConfigTransId,
            controlMeasureProperty.PBAVARNOREL,
            controlMeasureProperty.DATAAREAID,
            valueState
        ).subscribe({
            next: (response) => {
                if (!response.AllOk) {
                    this.showError(response.Message)
                }
            }
        })
    }

    // DELETE CONTROL MEASURE
    deleteSelectedProperty_ControlMeasure(controlMeasureProperty: any) {

        this.controlMeasureApiService.deleteControlMeasureData(this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId, controlMeasureProperty.PBAVARNOREL, controlMeasureProperty.DATAAREAID)
            .subscribe({
                next: (response) => {
                    if (response.AllOk) {

                        // Take action
                        this.onValueChanged()

                        // Reload from DB
                        // Call method on service
                        this.deletedProperty_ControlMeasure()
                    }
                    else {
                        this.showError(response.Message)
                    }
                }
            })
    }

    getConfigData(): ConfigData { return this._config.ConfigData; }

    setConfigData(configId, configTransId, configType, calledFromPbaVarNoRel, isDuplicated_FirstConfig, isChangeableOrder) {

        this._config.ConfigData = this.configData
        this._config.ConfigData.ConfigId = configId;
        this._config.ConfigData.ConfigType = configType;
        this._config.ConfigData.ConfigTransId = configTransId;
        this._config.ConfigData.IsSubConfig = this._config.IsSubConfig;
        this._config.ConfigData.IsDuplicated_FirstConfig = isDuplicated_FirstConfig;
        this._config.ConfigData.CalledFromPbaVarNoRel = calledFromPbaVarNoRel;
        this._config.ConfigData.IsChangeableOrder = isChangeableOrder;

        this.loadSalesXxxLine()
    }

    // Internal, load SalesXxxLine
    loadSalesXxxLine() {

        // Are we already loading line
        if (this._config.IsLoading_Line) {
            return;
        }

        // Parameters must be set
        if (this._config.ConfigData.ConfigId === '' || this._config.ConfigData.ConfigTransId === '' || this._config.ConfigData.ConfigType === '') {
            return;
        }

        // Flag that we start loading line
        this._config.IsLoading_Line = true;

        // Executes when we got a line
        const onSuccess_GetLine = (response) => {

            if (this._config.Line != undefined) {
                this._config.ConfigData.LineItemId = this._config.Line.ITEMID;
                this._config.ConfigData.State_ControlMeasure = this._config.Line.DELIVERY_STATUS_IS_IN_CONTROL_MEASURE_STATE;
                this._config.ConfigData.State_ControlMeasureOrLater = this._config.Line.DELIVERY_STATUS_IS_IN_OR_PAST_CONTROL_MEASURE_STATE
                this._config.ConfigData.State_ControlMeasure_Ready = this._config.Line.CAP_CONTROLMEASURESTATUS == ConstantsService.BasketLine.ControlMeasure.ENUM_READY_FOR_CONTROL_MEASURE;
                this._config.ConfigData.State_ControlMeasure_Started = this._config.Line.CAP_CONTROLMEASURESTATUS == ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_STARTED;
                this._config.ConfigData.State_ControlMeasure_Saved = this._config.Line.CAP_CONTROLMEASURESTATUS == ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_SAVED;
                this._config.ConfigData.State_ControlMeasure_Done = this._config.Line.CAP_CONTROLMEASURESTATUS == ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_DONE;
                this._config.ConfigData.State_ControlMeasure_AtLeastDone = this._config.Line.CAP_CONTROLMEASURESTATUS >= ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_DONE;
                this._config.ConfigData.State_ControlMeasure_ConfirmedBySeller = this._config.Line.CAP_CONTROLMEASURESTATUS == ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_CONFIRMED_BY_SELLER;
                this._config.ConfigData.State_ControlMeasure_AllValueStatesChanged = this._config.Line.CONTROL_MEASURE_VALUE_STATES_CHANGED;
                this._config.ConfigData.State_ControlMeasure_Finished = this._config.Line.CAP_CONTROLMEASURESTATUS >= ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_DONE;
                this._config.ConfigData.State_ControlMeasure_Head_Finished = this._config.Line.CONTROL_MEASURE_STATUS_BASKET != undefined ? this._config.Line.CONTROL_MEASURE_STATUS_BASKET == ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_DONE : false;
                this._config.ConfigData.State_ControlMeasure_Head_AtLeastFinished = this._config.Line.CONTROL_MEASURE_STATUS_BASKET != undefined ? this._config.Line.CONTROL_MEASURE_STATUS_BASKET >= ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_DONE : false;
                this._config.ConfigData.State_ControlMeasure_DisableInput = this._config.Line.CAP_CONTROLMEASURESTATUS >= ConstantsService.BasketLine.ControlMeasure.ENUM_CONTROL_MEASURE_SAVED;
                this._config.ConfigData.LineLetter = this._config.Line.CAP_LETTER;
                this._config.ConfigData.DisableInput =
                    this._config.DisableInput_Parent === true
                    || this._config.Line.LOCK_LINE_CONFIG === true
                    || (!this.isAssembler() && this._config.ConfigData.State_ControlMeasure_Started)//200903DL -->M1835_LåsaUppOffert
                    || this.userInternalService.accessLevel(this.getSalesBasket(), false, 20, this.getSalesTable()) < 1
                    || (this.isAssembler())


            }
            else {
                this._config.ConfigData.LineItemId = ''
                this._config.ConfigData.State_ControlMeasure = false
                this._config.ConfigData.State_ControlMeasureOrLater = false
                this._config.ConfigData.State_ControlMeasure_Ready = false
                this._config.ConfigData.State_ControlMeasure_Started = false
                this._config.ConfigData.State_ControlMeasure_Saved = false
                this._config.ConfigData.State_ControlMeasure_Done = false
                this._config.ConfigData.State_ControlMeasure_ConfirmedBySeller = false
                this._config.ConfigData.State_ControlMeasure_AllValueStatesChanged = false
                this._config.ConfigData.State_ControlMeasure_Finished = false
                this._config.ConfigData.State_ControlMeasure_Head_Finished = false
                this._config.ConfigData.State_ControlMeasure_DisableInput = false
                this._config.ConfigData.SelectedProperty_ControlMeasure_HasChangedValueState = false
                this._config.ConfigData.LineLetter = ''
                this._config.ConfigData.LineUnitPrice = 0
                this._config.ConfigData.LineTotalPrice = 0
                this._config.ConfigData.DisableInput = true
            }
            this._config.ConfigData.Line = this._config.Line
            this.getLinePrice()

            // Broadcast event that anyone interested of can pickup
            this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-TRANS-ID-CHANGED' });

            this._config.IsLoading_Line = false;
        }

        // Executes when there was an error getting an answer
        const onError_GetLine = (response) => {
            this._config.Line = undefined;
            this._config.IsLoading_Line = false;

            // Broadcast event that anyone interested of can pickup
            this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'SERVER-ERROR' })
        }

        // Load line
        const loadLine = () => {
            this.getLine(onSuccess_GetLine, onError_GetLine);
        }

        // Executes if user has access to basket
        const onSuccess_HasAccess = (axResponse) => {

            if (axResponse.AllOk) {
                this._config.ConfigData.HasAccessToLine = true;
                loadLine();
            }
        }

        const onError_HasAccess = (response) => {
            this._config.ConfigData.HasAccessToLine = false;
            this._config.IsLoading_Line = false;
        }

        // Do we already have access to line
        //if (_hasAccessTo_Line === true) {
        if (this._config.ConfigData.HasAccessToLine === true) {

            // Load line
            loadLine();
        }
        else {
            // Check that user has access to line (SalesBasketLine, SalesLine or SalesQuotationLine)
            this.line_CheckAccess(onSuccess_HasAccess, onError_HasAccess);
        }
    }

    hasControlMeasuredValues_VisualGroup(property) {
      return property != undefined && property.HAS_CONTROLMEASUREVALUES_VISUALGROUP === true;
    }

    hasControlMeasuredValue_ChangedState(property) {
      return property.HASCONTROLMEASURE_VALUE && (property.CONTROLMEASURE_VALUESTATE > ConstantsService.ControlMeasureValueState.NEW_VALUE);
    }

    // Method checking access to line
    line_CheckAccess(callbackAtSuccess?: Function, callbackAtError?: Function) {

        switch (this._config.ConfigData.ConfigType) {
            case 'sales':
                this.salesTableApiService.axSalesTableCheckAccess(this._config.ConfigData.ConfigId)
                    .subscribe({
                        next: (response) => {
                            if (callbackAtSuccess != undefined) {
                                callbackAtSuccess(response)
                            }
                        },
                        error: (err) => {
                            if (callbackAtError != undefined) {
                                callbackAtError('Kunde inte kontakta servern. Försök igen senare eller kontakta supporten om problemet kvarstår');
                            }
                        }
                    })
                break;

            case 'quotation':
                return   // Not implemented yet

            case 'webquotation':
                this.salesBasketApiService.checkAccess(this._config.ConfigData.ConfigId, false)
                    .subscribe({
                        next: (response) => {
                            if (callbackAtSuccess != undefined) {
                                callbackAtSuccess(response)
                            }
                        },
                        error: (err) => {
                            if (callbackAtError != undefined) {
                                callbackAtError('Kunde inte kontakta servern. Försök igen senare eller kontakta supporten om problemet kvarstår');
                            }
                        }
                    })
                break;
        }
    }

    // Get Entities from dataservice/Factory
    getLine(callbackAtSuccess?: Function, callbackAtError?: Function): void {

        if (this._config.ConfigData.HasAccessToLine === false) {
            return;
        }

        switch (this._config.ConfigData.ConfigType) {
            case 'sales':
                this.salesTableApiService.getSalesLine(this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId)
                    .subscribe({
                        next: (response) => {
                            this._config.Line = response

                            if (callbackAtSuccess != undefined) {
                                callbackAtSuccess()
                            }
                        },
                        error: (err) => {
                            if (callbackAtError != undefined) {
                                callbackAtError('Kunde inte kontakta servern. Försök igen senare eller kontakta supporten om problemet kvarstår');
                            }
                        }
                    })
                break;

            case 'quotation':
                return   // Not implemented yet

            case 'webquotation':
                this.salesBasketApiService.getSalesBasketLine(this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId)
                    .subscribe({
                        next: (response) => {
                            this._config.Line = response

                            if (callbackAtSuccess != undefined) {
                                callbackAtSuccess()
                            }
                        },
                        error: (err) => {
                            if (callbackAtError != undefined) {
                                callbackAtError('Kunde inte kontakta servern. Försök igen senare eller kontakta supporten om problemet kvarstår');
                            }
                        }
                    })

                break;
        }

        if (this._config.Line != undefined) {
            this._config.Line = undefined
        }
    }

    getLinePrice() {

        if (this._config.ConfigData.HasAccessToLine === false) {
            return;
        }

        switch (this._config.ConfigData.ConfigType) {
            case 'sales':
                this.salesTableApiService.getSalesLine(this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId)
                    .subscribe({
                        next: (response) => {
                            this.handleLineResponse(response)
                        },
                        error: (err) => {
                            console.error(err)
                        }
                    })
                break

            case 'quotation':
                break

            case 'webquotation':
                this.salesBasketApiService.getSalesBasketLine(this._config.ConfigData.ConfigId, this._config.ConfigData.ConfigTransId)
                    .subscribe({
                        next: (response) => {
                            this.handleLineResponse(response)
                        },
                        error: (err) => {
                            console.error(err)
                        }
                    })
                break
        }
    }

    private handleLineResponse(response) {
        this._config.Line = response
        this._config.ConfigData.LineUnitPrice = response.SALESPRICE;
        this._config.ConfigData.LineTotalPrice = response.SALESPRICE * this._config.Line.SALESQTY;
        // 191014PO --> Use CAP_SALESPRICE_INCLSUBLINES if set
        if (response.CAP_SALESPRICE_INCLSUBLINES && response.CAP_SALESPRICE_INCLSUBLINES != 0) {
            this._config.ConfigData.LineUnitPrice = response.CAP_SALESPRICE_INCLSUBLINES;
            this._config.ConfigData.LineTotalPrice = response.CAP_SALESPRICE_INCLSUBLINES * this._config.Line.SALESQTY;
        }

        if (this._config.ConfigData.ConfigType == 'sales') {
            this._config.ConfigData.LinePriceIsPreliminary = response.CAP_STATUS_SALESPRICE == 0;
            if (this._config.ConfigData.LinePriceIsPreliminary) {
                this._config.ConfigData.LineUnitPrice = response.PRELIMINARY_LINE_PRICE;
                this._config.ConfigData.LineTotalPrice = response.PRELIMINARY_LINE_PRICE * this._config.Line.SALESQTY;
            }
        }
    }

    showMessage(message: string, messageType: any) {
        this.clearIsDoingMessage()

        if (message == undefined || message == null) {
            message = '';
        }

        switch (messageType) {
            case this.messageTypes.INFO:
                this._config.ConfigData.InfoMessage = message;
                break;
            case this.messageTypes.WARNING:
                this._config.ConfigData.WarningMessage = message;
                break;
            case this.messageTypes.ERROR:
                this._config.ConfigData.ErrorMessage = message;
                break;
        }

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'SHOW-MESSAGE' });
    }

    clearMessage(messageType) {
        switch (messageType) {
            case this.messageTypes.INFO:
                this._config.ConfigData.InfoMessage = '';
                break;
            case this.messageTypes.WARNING:
                this._config.ConfigData.WarningMessage = '';
                break;
            case this.messageTypes.ERROR:
                this._config.ConfigData.ErrorMessage = '';
                break;
        }

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'SHOW-MESSAGE' });
    }

    showInfo(message: string) {
        this.showMessage(message, this.messageTypes.INFO);
    }
    showWarning(message: string) {
        this.showMessage(message, this.messageTypes.WARNING);
    }
    showError(message: string) {
        this.showMessage(message, this.messageTypes.ERROR);
    }
    clearInfo() {
        this.clearMessage(this.messageTypes.INFO);
    }
    clearWarning() {
        this.clearMessage(this.messageTypes.WARNING);
    }
    clearError() {
        this.clearMessage(this.messageTypes.ERROR);
    }

    changeableOrderQuestion(newValue, property, newDisplayValue, executedFromControlMeasureProperty) {

        const onAnswerYes_ChangeableOrder_Approval = () => {
            const salesId = this._config.ConfigData.ConfigId;
            const inventTransId = this._config.ConfigData.ConfigTransId;
            const pbaVarNoRel = property.PBAVARNOREL;
            const actionText = headerText;
            const messageText = questionText.join();
            userApproved = true;

            // Save/Log/Resolve message that the user approved or disapproved
            this.salesTableApiService.changableOrderResolveMessage(salesId, inventTransId, pbaVarNoRel, actionText, messageText, userApproved, ConstantsService.ExceptionTypeEnum.WARNING)
                .subscribe({
                    next: (response) => {
                        const executingFromCallback_ChangeableOrder = true

                        if (userApproved) {
                            // Call _validate with new value
                            return this.validateNewValue(newValue, property, newDisplayValue, executedFromControlMeasureProperty, executingFromCallback_ChangeableOrder);
                        }
                        else {
                            // Call _validate with PREVIOUS value (will make the config reload to reset the previous value)
                            return this.validateNewValue(property.VALUE, property, newDisplayValue, executedFromControlMeasureProperty, executingFromCallback_ChangeableOrder);
                        }
                    },
                    error: (err) => {
                        const executingFromCallback_ChangeableOrder = true;
                        // Call _validate with PREVIOUS value (will make the config reload to reset the previous value)
                        return this.validateNewValue(property.VALUE, property, newDisplayValue, executedFromControlMeasureProperty, executingFromCallback_ChangeableOrder);
                    }
                })
        }

        const onAnswerNo_ChangeableOrder_Approval = () => {
            const salesId = this._config.ConfigData.ConfigId;
            const inventTransId = this._config.ConfigData.ConfigTransId;
            const pbaVarNoRel = property.PBAVARNOREL;
            const actionText = headerText;
            const messageText = questionText.join();
            userApproved = false;

            // Save/Log/Resolve message that the user approved or disapproved
            this.salesTableApiService.changableOrderResolveMessage(salesId, inventTransId, pbaVarNoRel, actionText, messageText, userApproved, ConstantsService.ExceptionTypeEnum.INFO)
                .subscribe({
                    next: (response) => {
                        const executingFromCallback_ChangeableOrder = true

                        if (userApproved) {
                            // Call _validate with new value
                            return this.validateNewValue(newValue, property, newDisplayValue, executedFromControlMeasureProperty, executingFromCallback_ChangeableOrder);
                        }
                        else {
                            // Call _validate with PREVIOUS value (will make the config reload to reset the previous value)
                            return this.validateNewValue(property.VALUE, property, newDisplayValue, executedFromControlMeasureProperty, executingFromCallback_ChangeableOrder);
                        }
                    },
                    error: (err) => {
                        const executingFromCallback_ChangeableOrder = true;
                        // Call _validate with PREVIOUS value (will make the config reload to reset the previous value)
                        return this.validateNewValue(property.VALUE, property, newDisplayValue, executedFromControlMeasureProperty, executingFromCallback_ChangeableOrder);
                    }
                })
        }

        let userApproved = false

        const previousValue = property.DATATYPESTR.toLowerCase() == "text" || property.DATATYPESTR.toLowerCase() == "number" ? property.VALUE : property.DISPLAYVALUE;
        const headerText = "Ändra variabelvärde";

        const questionText = [
            'Det värde du ändrar nu kan påverka leveranstiden av produkten.',
            '',
            `Variabel: ${property.LABEL}`,
            `Från: ${previousValue}`,
            `Till: ${(newDisplayValue == null || newDisplayValue == '' ? newValue : newDisplayValue)}`,
            '',
            'Vill du fortsätta?'];

        this._modalYesNoDynamic(headerText, questionText, 'Ja', 'Avbryt', onAnswerYes_ChangeableOrder_Approval.bind(this), onAnswerNo_ChangeableOrder_Approval.bind(this))
    }

    validateNewValue(newValue: string, property: Property, newDisplayValue: string, executedFromControlMeasureProperty: boolean = false, executedFromCallback_ChangeableOrder: boolean = false) {

        const pbaVarNoRel = property.PBAVARNOREL;
        const dataAreaId_PbaVarNoRel = property.DATAAREAID;

        // If this is a Changeable Order
        // And we're not executing this as a callback from.
        if (this._config.ConfigData.IsChangeableOrder && !executedFromCallback_ChangeableOrder) {

            const lineHas_ShippingDateConfirmed = this._config.Line.SHIPPINGDATECONFIRMED != ConstantsService.Common.MINIMUM_DATETIME_AX;

            if (property.BREAKS_DLVCALC && lineHas_ShippingDateConfirmed) {
                this.changeableOrderQuestion(newValue, property, newDisplayValue, executedFromControlMeasureProperty);
                return;
            }
        }

        if (this._config.ConfigData.IsValidatingValue) {
            return;
        }

        this.onIsValidatingValue()

        // Call to AX to validate the ModelConfigVar
        // if AX descides it needs to run the entire config, it will do it automatically, and notify us in the response that it did so
        this.figureconfigApiService.validateModelConfigVar(this._config.ConfigData.basket.SALESBASKETID, this._config.ConfigData.ConfigTransId, pbaVarNoRel, newValue, dataAreaId_PbaVarNoRel, newDisplayValue, this._config.ConfigData.CalledFromPbaVarNoRel)
            .pipe(
                take(1)
            ).subscribe({
                next: (response) => {
                    this.onValidationModelConfigSuccess(response, executedFromControlMeasureProperty)
                },
                error: (err) => {
                    this._config.ConfigData.IsValidatingValue = false
                    console.error('validateModelConfigVar', JSON.stringify(err))
                }
            })
    }

    private onValidationModelConfigSuccess(response, executedFromControlMeasureProperty: boolean) {
        // Get response from AX
        const figureConfigResponse = response

        // Show/Hide InfoMessage
        if (figureConfigResponse?.InfoMessage) {
            this.showInfo(figureConfigResponse.InfoMessage);
        }
        else {
            this.clearInfo();
        }

        // Show/hide WarningMessage
        if (figureConfigResponse.WarningMessage !== '') {
            this.showWarning(figureConfigResponse.WarningMessage);
        }
        else {
            this.clearWarning();
        }

        // Though AX didn't like it
        if (figureConfigResponse.IsValid === false || this._config.ConfigData.ConfigTransId !== figureConfigResponse.InventTransId) {

            // Show any errors
            this.onValueNotValid(figureConfigResponse.Message);
        }

        // He, AX did like our change
        if (figureConfigResponse.IsValid === true) {

            if (executedFromControlMeasureProperty) {
                this.setSelectedProperty_ControlMeasure(undefined);
            }

            // Take action
            this.onValueChanged();
        }
    }

    onValueChanged() {
        const property = this._config.ConfigData.SelectedProperty;
        let reload = true;

        //Displacement properties kör en del egna metoder och har ingen vald property så om displacement sektionen är öppen kör vi alltid reload
        if (property && this._config.ConfigData.SelectedDisplacementVarRecId === 0) {
            reload = this.PBAVARNOREL_NO_RELOAD.indexOf(property.PBAVARNOREL) < 0;
            if (!reload && property.CONFIG_NEEDED) {
                this._config.ConfigData.SelectedProperty.CONFIG_NEEDED = false;
            }
        }

        if (reload) {
            this.loadSalesXxxLine();
        } else {
            //Load line without reloading everything
            this.getLinePrice();
        }

        this._config.ConfigData.IsValidatingValue = false;
        this.clearIsDoingMessage();

        this._config.HasChanged = true;

        // If selected property configures ItemId
        if (property && property.IsProperty_ItemIdConfig === true) {

            // We need to unselect any selected fields/displacementVars
            this._config.ConfigData.SelectedField = '';
            this._config.ConfigData.SelectedAreaRecId = 0;
            this._config.ConfigData.SelectedDisplacementVarRecId = 0;
        }

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'VALUE-VALIDATION-OK' });
    }

    onValueNotValid(errorMessage: string) {
        this._config.ConfigData.IsValidatingValue = false;

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'VALUE-VALIDATION-NOT-OK' });

        this.showError(errorMessage);
    }

    onIsValidatingValue() {
        this.clearError();
        this.showIsDoingMessage('VALIDERAR VÄRDE');
        this._config.ConfigData.IsValidatingValue = true;

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'VALUE-VALIDATION-IS-VALIDATING' });
    }

    toggleShowMeasurements() {
        this._config.ConfigData.ShowMeasurements = !this._config.ConfigData.ShowMeasurements;

        // Broadcast event that anyone interested of can pickup
        this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'SHOW-MEASUREMENTS-CHANGED' });
    }

    setHasSetConfig(hasSetConfig: boolean, setName: string) {
        if (this._config && this._config.ConfigData) {
            this._config.ConfigData.HasSetConfig = hasSetConfig;
            this._config.ConfigData.SetName = setName;

            // Broadcast event that anyone interested of can pickup
            this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-DATA-CHANGED' });
        }
    }

    setVisualGroupId(visualGroupId) {
        if (this._config && this._config.ConfigData) {
            this._config.ConfigData.VisualGroupId = parseInt(visualGroupId);

            // Broadcast event that anyone interested of can pickup
            this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-DATA-CHANGED' });
        }
    }

    setHasDisplacementWarnings(hasDisplacementWarnings: boolean) {
        if (this._config && this._config.ConfigData) {
            this._config.ConfigData.HasDisplacementWarnings = hasDisplacementWarnings;

            // Broadcast event that anyone interested of can pickup
            this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-DATA-CHANGED' });
        }
    }

    setConfigImageMouseEvent(mouseEvent?: any, configImageX?: number, configImageY?: number) {
        if (this._config && this._config.ConfigData) {
            if (mouseEvent) {
                this._config.ConfigData.MouseEvent = mouseEvent;
                this._config.ConfigData.MouseEvent_ConfigImageX = configImageX;
                this._config.ConfigData.MouseEvent_ConfigImageY = configImageY;
            }
            else {
                this._config.ConfigData.MouseEvent = undefined;
                this._config.ConfigData.MouseEvent_ConfigImageX = -1;
                this._config.ConfigData.MouseEvent_ConfigImageY = -1;
            }

            // Broadcast event that anyone interested of can pickup
            this.publishSubscribeService.trigger<ConfigEvent>(Events.FigureConfigService.CONFIG_DATA_EVENT, { configData: this._config.ConfigData, event: 'CONFIG-IMAGE-MOUSE-EVENT' });
        }
    }

    getSalesBasket() { return this._config.SalesBasket; }
    getSalesTable() { return this._config.SalesTable; }

    // MODAL - YES / NO DYNAMIC QUESTION
    private _modalYesNoDynamic(headerText: string, questionText: string[], yesText: string, noText: string, callbackWhenYes?: Function, callbackWhenNo?: Function, callbackWhenCancelled?: Function) {

        const initialState: object = { title: headerText, description: questionText, acceptBtnTitle: yesText, abortBtnTitle: noText, closeAvailable: false }

        const modalRef: BsModalRef = this.modalService.show(YesNoComponent, { initialState })

        const content = modalRef.content as YesNoComponent;

        // Subscribe to the data emitted by the modal (subscription is completed in YesNoModalComponent when user submit an answer, no need to use takeUntil/takeUntiDestroyed)
        content.onClose.subscribe((result: boolean | null) => {
            if (result != null) {
                if (result == true) {
                    if (callbackWhenYes !== undefined) {
                        callbackWhenYes();
                        return;
                    }
                }
                if (result == false) {
                    if (callbackWhenNo !== undefined) {
                        callbackWhenNo();
                    }
                }
            } else {
                if (callbackWhenCancelled !== undefined) {
                    callbackWhenCancelled();
                }
            }
        });
    }

    isUpdatingLineDescriptions(isUpdating: boolean | undefined = undefined) {
        if (isUpdating != undefined) {
            this.updatingLineDescriptions = isUpdating;
            this.publishSubscribeService.trigger<boolean>(Events.FigureConfigService.IS_UPDATING_LINE_DESCRIPTION, this.updatingLineDescriptions);
        }

        return this.updatingLineDescriptions;
    }

    getUValue(basketTransId): Observable<string> {
        return this.figureconfigApiService.calculateUValue(basketTransId)
            .pipe(
                map((data) => {
                    const returnValue = data ? JSON.parse(data.ReturnValue) : undefined;

                    if (returnValue && data?.IsValid && returnValue?.HasValidUValue) {
                        return returnValue.Energy_Uw.replace(',', '.');
                    } else {
                        throw new Error("Beräkningsunderlag saknas.");
                    }
                }),
                catchError((err) => {
                    return of(err.message || 'Beräkningsunderlag saknas.')
                })
            )
    }
}
